import { Box, Collapse, Fade, useTheme } from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';

type Props = {
  connected: boolean;
  editable: ReactNode;
  readOnly: ReactNode;
  enterDelay?: number;
};

export const ListingConnectSwap = ({
  connected,
  editable,
  readOnly,
  enterDelay = 0,
}: Props) => {
  const theme = useTheme();
  const duration = theme.transitions.duration.standard;

  // `collapsed` drives the editable collapse-out (delayed by `enterDelay` so a
  // card can trail another). `showReadOnly` starts the read-only reveal partway
  // through that collapse, so the new content cross-fades in while the old is
  // still on its way out instead of waiting for it to fully disappear. Both
  // initialize to `connected` so an already-linked listing renders read-only on
  // mount with no transition.
  const [collapsed, setCollapsed] = useState(connected);
  const [showReadOnly, setShowReadOnly] = useState(connected);

  useEffect(() => {
    if (!connected) {
      setCollapsed(false);
      setShowReadOnly(false);
      return;
    }
    const collapseTimer = setTimeout(() => setCollapsed(true), enterDelay);
    const revealTimer = setTimeout(
      () => setShowReadOnly(true),
      enterDelay + duration / 2,
    );
    return () => {
      clearTimeout(collapseTimer);
      clearTimeout(revealTimer);
    };
  }, [connected, enterDelay, duration]);

  return (
    <>
      <Collapse in={!collapsed} timeout={duration} unmountOnExit>
        <Fade in={!collapsed} timeout={duration}>
          <Box>{editable}</Box>
        </Fade>
      </Collapse>
      <Collapse in={showReadOnly} timeout={duration} unmountOnExit>
        <Fade in={showReadOnly} timeout={duration}>
          <Box>{readOnly}</Box>
        </Fade>
      </Collapse>
    </>
  );
};
